import React, { useEffect, useState } from 'react';
import { useLocation, useHistory, Link } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Pagination, { getStoredPageSize } from '../../components/Pagination/Pagination';
import Footer from '../../components/Footer/Footer';
import { getMonitors, deleteMonitor, startMonitor, stopMonitor } from '../../services/MonitorsService';
import { getStrategiesUsage } from '../../services/StrategiesService';
import { getItemsHealth } from '../../services/BeholderService';
import { processError } from '../../services/ServiceUtils';
import MonitorRow from './MonitorRow';
import MonitorModal from './MonitorModal/MonitorModal';
import Toast from '../../components/Toast/Toast';
import LogModal from '../../components/Logs/LogModal';
import NewMonitorButton from './NewMonitorButton';
import CompletarMonitoresButton from './CompletarMonitoresButton';
import ScannerResults from './ScannerResults';
import HowItWorks from '../../components/HowItWorks/HowItWorks';
import { getEmptyMessage, getErrorMessage } from '../../utils/MessageUtils';
import { findRowFromActionButton, getIdFromActionButton } from '../../utils/DataRowButtonUtils';
import QuickSetupBanner from '../../components/QuickSetupBanner/QuickSetupBanner';
import SectionHealthBanner from '../../components/SectionHealthBanner/SectionHealthBanner';
import OnlyActiveToggle from '../../components/OnlyActiveToggle/OnlyActiveToggle';
import { getOnlyActive, setOnlyActive as storeOnlyActive } from '../../utils/OnlyActiveStorage';
import ViewModeToggle, { getStoredViewMode } from '../../components/ViewModeToggle/ViewModeToggle';

const MONITOR_ISSUE_IDS = ['no_scanner', 'no_regime', 'no_candles', 'scanner_stopped', 'regime_stopped', 'candles_stopped'];

function Monitors() {

    const defaultLocation = useLocation();
    const history = useHistory();

    function getPage(location) {
        if (!location) location = defaultLocation;
        return new URLSearchParams(location.search).get('page');
    }

    function getSize(location) {
        if (!location) location = defaultLocation;
        return parseInt(new URLSearchParams(location.search).get('size')) || getStoredPageSize();
    }

    useEffect(() => {
        return history.listen(location => {
            setPage(getPage(location));
            setSize(getSize(location));
        });
    }, [history]); // eslint-disable-line react-hooks/exhaustive-deps

    const [count, setCount] = useState(0);
    const [page, setPage] = useState(getPage());
    const [size, setSize] = useState(getSize());
    const [monitors, setMonitors] = useState([]);
    const [usageMap, setUsageMap] = useState({});
    const [healthMap, setHealthMap] = useState({});
    const [onlyActive, setOnlyActiveState] = useState(getOnlyActive('monitors'));
    // Modo Simples (padrão): só monitores avulsos, escondendo o que é gerenciado por estratégia.
    const [viewMode, setViewMode] = useState(getStoredViewMode('monitorsViewMode'));
    const [hiddenCount, setHiddenCount] = useState(0);

    const [notification, setNotification] = useState(getEmptyMessage());

    const [editMonitor, setEditMonitor] = useState({});

    useEffect(() => {
        getMonitors(page || 1, size, onlyActive, viewMode === 'simple')
            .then(result => {
                setMonitors(result.rows);
                setCount(result.count);
                setHiddenCount(result.hiddenCount || 0);
            })
            .catch(err => setNotification(getErrorMessage(processError(err))))
    }, [page, size, onlyActive, viewMode])

    function onViewModeChange(mode) {
        setViewMode(mode);
        const params = new URLSearchParams(defaultLocation.search);
        params.set('page', '1');
        history.push({ search: params.toString() });
    }

    function onToggleActive(value) {
        storeOnlyActive('monitors', value);
        setOnlyActiveState(value);
        const params = new URLSearchParams(defaultLocation.search);
        params.set('page', '1');
        history.push({ search: params.toString() });
    }

    useEffect(() => {
        getStrategiesUsage()
            .then(map => setUsageMap(map.monitors || {}))
            .catch(() => {});
    }, []);

    useEffect(() => {
        getItemsHealth()
            .then(map => setHealthMap(map.monitors || {}))
            .catch(() => {});
    }, []);

    function onEditClick(event) {
        setEditMonitor(findRowFromActionButton(event, monitors));
    }

    function onLogsClick(event) {
        setEditMonitor(findRowFromActionButton(event, monitors));
    }

    function onStopClick(event) {
        stopMonitor(getIdFromActionButton(event))
            .then(monitor => { history.go(0) })
            .catch(err => setNotification(getErrorMessage(processError(err))))
    }

    function onStartClick(event) {
        startMonitor(getIdFromActionButton(event))
            .then(monitor => { history.go(0) })
            .catch(err => setNotification(getErrorMessage(processError(err))))
}

    function onDeleteClick(event) {
        deleteMonitor(getIdFromActionButton(event))
            .then(monitor => { history.go(0) })
            .catch(err => setNotification(getErrorMessage(processError(err))))
    }

    function onModalSubmit(event) {
        history.go(0);
    }

    return (
        <React.Fragment>
            <Menu />
            <main className='content'>
                <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4'>
                    <div className='d-block mb-4 mb-md-0'>
                        <h2 className='h4'>Monitores</h2>
                    </div>
                    <div className='btn-toolbar mb-2 mb-md-0 align-items-center'>
                        <ViewModeToggle mode={viewMode} onChange={onViewModeChange} storageKey='monitorsViewMode' />
                        <OnlyActiveToggle id='monitorsOnlyActive' checked={onlyActive} onChange={onToggleActive} />
                        <div className='d-inline-flex align-items-center'>
                            <CompletarMonitoresButton onSuccess={() => history.go(0)} />
                            <NewMonitorButton />
                        </div>
                    </div>
                </div>
                <HowItWorks>
                    <p className='mb-1'><strong>Monitores</strong> coletam dados do mercado em tempo real e alimentam as automações.</p>
                    <ul className='mb-0 ps-3'>
                        <li><strong>Candles</strong> — calcula indicadores técnicos (EMA, RSI, MACD...) para um símbolo e intervalo.</li>
                        <li><strong>Regime de Mercado</strong> — classifica o mercado em Tendência de Alta, Tendência de Baixa, Lateral ou Alta Volatilidade.</li>
                        <li><strong>Scanner de Mercado</strong> — varre múltiplos ativos periodicamente e identifica os mais promissores.</li>
                        <li><strong>Funding Rate</strong> — monitora a taxa de financiamento de contratos perpétuos.</li>
                    </ul>
                    <p className='mt-2 mb-0 text-warning'>Sem monitores ativos, as automações não recebem dados e não disparam.</p>
                </HowItWorks>
                <SectionHealthBanner filterIds={MONITOR_ISSUE_IDS} title='Monitores com pendências' />
                {monitors && monitors.length === 0 && count === 0 && hiddenCount === 0 && (
                    <QuickSetupBanner onSuccess={() => history.go(0)} />
                )}
                {hiddenCount > 0 && (
                    <div className='card border-0 shadow mb-3'>
                        <div className='card-body py-3'>
                            <p className='mb-0'>
                                🧩 <strong>{hiddenCount}</strong> monitor(es) gerenciado(s) pelas suas estratégias não {hiddenCount === 1 ? 'aparece' : 'aparecem'} nesta visão — gerencie pela tela <Link to='/strategies'>Estratégias</Link>.
                            </p>
                            <small className='text-muted'>Uma estratégia agrupa o monitor e as automações de compra/venda em uma peça só, pra você não precisar mexer nelas separadamente.</small>
                        </div>
                    </div>
                )}
                {monitors && monitors.length === 0 && hiddenCount > 0 ? (
                    <div className='card border-0 shadow'>
                        <div className='card-body text-center py-4'>
                            <p className='text-muted mb-0'>Você não tem monitores avulsos — tudo que roda aqui é gerenciado pelas suas estratégias.</p>
                        </div>
                    </div>
                ) : (
                    <div className='card card-body border-0 shadow table-wrapper table-responsive'>
                        <table className='table table-hover'>
                            <thead>
                                <tr>
                                    <th className='border-gray-200'>Tipo</th>
                                    <th className='border-gray-200'>Símbolo</th>
                                    <th className='border-gray-200'>Status</th>
                                    <th className='border-gray-200'>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    monitors && monitors.length
                                    ? monitors.map(monitor => (
                                        <MonitorRow key={monitor.id} data={monitor} usages={usageMap[monitor.id]} health={healthMap[monitor.id]} onEditClick={onEditClick} onStopClick={onStopClick} onStartClick={onStartClick} onDeleteClick={onDeleteClick} onLogsClick={onLogsClick} />
                                    ))
                                    : <React.Fragment></React.Fragment>
                                }
                            </tbody>
                        </table>
                        <Pagination count={count} size={size} />
                    </div>
                )}
                <ScannerResults />
                <Footer />
            </main>
            <MonitorModal data={editMonitor} onSubmit={onModalSubmit} />
            <LogModal file={editMonitor.id > 0 ? 'M_' + editMonitor.id : ''} />
            <Toast type={notification.type} text={notification.text} />
        </React.Fragment>
    )
}

export default Monitors;